import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Bot, AlertTriangle } from 'lucide-react';
import { useChat } from '../hooks/useChat';
import ReactMarkdown from 'react-markdown';
import { useChatContext } from '../context/ChatContext';
import remarkFlexibleMarkers from 'remark-flexible-markers';

const WIDGET_WIDTH  = 360;
const WIDGET_HEIGHT = 480;

const s = {
  fab: (open) => ({
    position: 'fixed', bottom: '24px', right: '24px',
    width: '52px', height: '52px', borderRadius: '50%',
    background: '#fd5f0d', color: '#fff', border: 'none',
    cursor: 'pointer', zIndex: 900,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(253,95,13,0.35)',
    transition: 'transform 0.2s',
    transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
  }),
  badge: {
    position: 'absolute', top: '-2px', right: '-2px',
    width: '16px', height: '16px', borderRadius: '50%',
    background: '#f03e3e', fontSize: '10px', fontWeight: '700',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '2px solid #fff',
  },
  panel: (open) => ({
    position: 'fixed', bottom: '88px', right: '24px',
    width: `${WIDGET_WIDTH}px`, height: `${WIDGET_HEIGHT}px`,
    background: '#fff', borderRadius: '14px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    display: 'flex', flexDirection: 'column',
    zIndex: 899, overflow: 'hidden',
    opacity: open ? 1 : 0,
    pointerEvents: open ? 'all' : 'none',
    transform: open ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
    transition: 'all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
    transformOrigin: 'bottom right',
  }),
  header: {
    background: '#fd5f0d', color: '#fff',
    padding: '14px 16px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', flexShrink: 0,
  },
  headerLeft:  { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar:      { width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { fontSize: '14px', fontWeight: '700' },
  headerSub:   { fontSize: '11px', opacity: 0.8 },
  closeBtn:    { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', opacity: 0.8, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  messages:    { flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', background: '#f8f9fb' },
  bubble: (role) => ({
    maxWidth: '82%', padding: '9px 13px',
    borderRadius: role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
    background: role === 'user' ? '#fd5f0d' : '#fff',
    color: role === 'user' ? '#fff' : '#1a1a1a',
    fontSize: '12.5px', lineHeight: '1.55', whiteSpace: 'pre-wrap',
    alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
    border: role === 'assistant' ? '1px solid #e8e8e8' : 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  }),
  typing: {
    alignSelf: 'flex-start', padding: '9px 14px', borderRadius: '12px 12px 12px 2px',
    background: '#fff', border: '1px solid #e8e8e8', fontSize: '16px', letterSpacing: '3px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  footer:    { padding: '10px 12px', borderTop: '1px solid #eee', background: '#fff', flexShrink: 0 },
  inputRow:  { display: 'flex', gap: '8px', alignItems: 'center' },
  input:     { flex: 1, padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '20px', fontSize: '12.5px', outline: 'none', background: '#f8f9fb' },
  sendBtn: (dis) => ({
    width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
    background: dis ? '#fed9c0' : '#fd5f0d', color: '#fff', border: 'none',
    cursor: dis ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }),
  footerNote: { fontSize: '10px', color: '#bbb', textAlign: 'center', marginTop: '6px' },
};

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen]     = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef           = useRef(null);

  const isAskPage = location.pathname === '/ask';

  const { messages, input, setInput, loading, sendMessage, handleKey, reset } = useChatContext();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!open && last?.role === 'assistant' && messages.length > 1) {
      setUnread(n => n + 1);
    }
  }, [messages]);

  const handleOpen = () => {
    setOpen(o => !o);
    if (!open) setUnread(0);
  };

  if (isAskPage) return null;

  return (
    <>
      {/* FAB */}
      <button style={s.fab(open)} onClick={handleOpen} title="Ask RAAH Assistant">
        {open ? <X size={22} strokeWidth={2} /> : <MessageCircle size={22} strokeWidth={2} />}
        {!open && unread > 0 && <span style={s.badge}>{unread}</span>}
      </button>

      {/* Widget panel */}
      <div style={s.panel(open)}>
        <div style={s.header}>
          <div style={s.headerLeft}>
            <div style={s.avatar}><Bot size={18} strokeWidth={1.75} /></div>
            <div>
              <div style={s.headerTitle}>RAAH Assistant</div>
              <div style={s.headerSub}>{loading ? 'Typing…' : 'Online'}</div>
            </div>
          </div>
          <button style={s.closeBtn} onClick={() => setOpen(false)}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div style={s.messages}>
          {messages.map((m, i) => (
            <div key={i} style={s.bubble(m.role)}>
              {m.isError && <AlertTriangle size={13} style={{ color: '#c92a2a', marginBottom: '4px' }} />}
              <ReactMarkdown
                remarkPlugins={[remarkFlexibleMarkers]}
                components={{
                  mark: ({ children }) => (
                    <mark style={{ background: '#fff176', borderRadius: '3px', padding: '0 2px' }}>
                      {children}
                    </mark>
                  ),
                  a: ({ href, children }) => (
                    <a     
                      href={href}
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {m.text}
              </ReactMarkdown>
            </div>
          ))}
          {loading && <div style={s.typing}>•••</div>}
          <div ref={bottomRef} />
        </div>

        <div style={s.footer}>
          <div style={s.inputRow}>
            <input
              style={s.input}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask anything…"
              disabled={loading}
            />
            <button style={s.sendBtn(loading)} onClick={() => sendMessage()} disabled={loading}>
              <Send size={15} strokeWidth={2} />
            </button>
          </div>
          <div style={s.footerNote}>Powered by RAAH AI · <a href="/ask" style={{ color: '#bbb' }}>Open full chat</a></div>
        </div>
      </div>
    </>
  );
}